import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatDate, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/ImageUpload";

const EMPTY = { title: "", excerpt: "", category: "Giardinaggio", image: "", content: "", published: true };

export default function PostsManager() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/posts/all").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, excerpt: p.excerpt, category: p.category, image: p.image, content: p.content, published: p.published });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/posts/${editing.id}`, form);
      else await api.post("/posts", form);
      toast.success(editing ? "Articolo aggiornato" : "Articolo pubblicato");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/posts/${id}`).catch(() => {});
    toast.success("Articolo eliminato");
    load();
  };

  const field = "border-line bg-sand/70 text-forest";

  return (
    <section data-testid="posts-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-3xl text-forest">Articoli del blog</h2>
        <button onClick={openNew} data-testid="post-new-button" className="flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 font-accent text-xs uppercase tracking-[0.18em] text-bone transition-transform duration-300 hover:scale-105 active:scale-95">
          <Plus className="h-4 w-4" /> Nuovo articolo
        </button>
      </div>
      <div className="mt-8 overflow-x-auto rounded-sm border border-line bg-bone">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-line font-accent text-[10px] uppercase tracking-[0.2em] text-sage">
              <th className="px-5 py-4">Titolo</th>
              <th className="hidden px-5 py-4 md:table-cell">Categoria</th>
              <th className="hidden px-5 py-4 md:table-cell">Data</th>
              <th className="px-5 py-4">Stato</th>
              <th className="px-5 py-4 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-b border-line/60 last:border-0" data-testid={`post-row-${p.id}`}>
                <td className="px-5 py-4 font-medium text-forest">{p.title}</td>
                <td className="hidden px-5 py-4 text-sage md:table-cell">{p.category}</td>
                <td className="hidden px-5 py-4 text-sage md:table-cell">{formatDate(p.created_at)}</td>
                <td className="px-5 py-4">
                  <span className={`rounded-full px-3 py-1 font-accent text-[10px] uppercase tracking-[0.15em] ${p.published ? "bg-moss/15 text-moss" : "bg-sand text-sage"}`}>
                    {p.published ? "Pubblicato" : "Bozza"}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(p)} className="rounded-full border border-line p-2 text-forest hover:bg-sand" data-testid={`post-edit-${p.id}`} aria-label="Modifica"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(p.id)} className="rounded-full border border-line p-2 text-destructive hover:bg-sand" data-testid={`post-delete-${p.id}`} aria-label="Elimina"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {posts.length === 0 && <p className="px-5 py-10 text-center text-sage">Nessun articolo. Crea il primo.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-bone" data-testid="post-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest">{editing ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-5 pt-2">
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Titolo *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`mt-2 ${field}`} data-testid="post-form-title" />
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Categoria</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`mt-2 ${field}`} data-testid="post-form-category" />
              </div>
            </div>
            <ImageUpload label="Immagine di copertina" value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="post-form-image" />
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Anteprima</Label>
              <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={`mt-2 ${field}`} data-testid="post-form-excerpt" />
            </div>
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Contenuto</Label>
              <Textarea rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={`mt-2 ${field}`} data-testid="post-form-content" />
            </div>
            <label className="flex items-center gap-3 text-sm text-forest">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="post-form-published" /> Pubblicato
            </label>
            <button onClick={save} disabled={saving || !form.title} data-testid="post-form-save" className="flex items-center justify-center gap-2 rounded-full bg-forest py-3.5 font-accent text-sm uppercase tracking-[0.2em] text-bone transition-transform duration-300 hover:scale-[1.01] active:scale-95 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salva
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
